/**
 * Pure, unit-tested logic behind OffersFunnelPage: splitting GET /api/offers/funnel's rows
 * into the OPEN progression vs. the WON/LOST outcome pair, bar-width
 * percentages for either metric (count or amount — never both encoded in
 * one bar, one axis per chart), and the headline
 * conversion-to-WON percentage. Kept out of the page component so the math
 * is testable without React/DOM, mirroring offersBoard.ts/offerPipeline.ts.
 *
 * Rows arrive from the backend already sorted by stage `position`
 * (OfferStageService.funnel/listAll are position-ordered) — FunnelRow has no
 * `position` field of its own, so these functions never re-sort; they only
 * filter by `kind` and preserve arrival order.
 */
import type { OfferFunnelRow } from "../api/offers";

export type FunnelMetric = "count" | "amount";

/** OPEN rows, in the order the backend returned them (already position-sorted). */
export function openFunnelRows(rows: OfferFunnelRow[]): OfferFunnelRow[] {
  return rows.filter((r) => r.kind === "OPEN");
}

/**
 * The single WON row, if the backend included one (invariant: exactly one
 * non-archived WON stage exists once seeded, and `funnel` includes every
 * active stage — see OfferService — so this is present whenever any
 * stage is).
 */
export function wonFunnelRow(rows: OfferFunnelRow[]): OfferFunnelRow | null {
  return rows.find((r) => r.kind === "WON") ?? null;
}

/** The single LOST row, if any — see {@link wonFunnelRow}. */
export function lostFunnelRow(rows: OfferFunnelRow[]): OfferFunnelRow | null {
  return rows.find((r) => r.kind === "LOST") ?? null;
}

/** Sum of `count` across every row (OPEN + WON + LOST) — the pipeline's total visible deal count. */
export function totalFunnelCount(rows: OfferFunnelRow[]): number {
  return rows.reduce((sum, r) => sum + r.count, 0);
}

/** Sum of `totalAmount` across every row. */
export function totalFunnelAmount(rows: OfferFunnelRow[]): number {
  return rows.reduce((sum, r) => sum + r.totalAmount, 0);
}

/**
 * Share of every currently-visible deal that reached WON, as a percentage
 * rounded to 1 decimal (0 when there are no deals at all — avoids a NaN from
 * dividing by zero). This is the pipeline's headline conversion figure.
 */
export function conversionToWonPct(rows: OfferFunnelRow[]): number {
  const total = totalFunnelCount(rows);
  if (total === 0) return 0;
  const won = wonFunnelRow(rows)?.count ?? 0;
  return Math.round((won / total) * 1000) / 10;
}

/** The largest metric value across `rows` — the bar-width denominator; floored at 1 to avoid a divide-by-zero when every row is empty. */
export function maxMetric(rows: OfferFunnelRow[], metric: FunnelMetric): number {
  const values = rows.map((r) => (metric === "count" ? r.count : r.totalAmount));
  return Math.max(1, ...values);
}

/**
 * `row`'s bar width as a percentage of the widest row for the given metric
 * (0-100, floored at 0). Encodes exactly one metric at a time — never both
 * count and amount in the same bar's length (the "one axis"
 * rule: two measures of different scale get two views, not one dual-coded
 * bar) — the *other* metric is still shown, just as plain adjacent text.
 */
export function barWidthPct(row: OfferFunnelRow, rows: OfferFunnelRow[], metric: FunnelMetric): number {
  const value = metric === "count" ? row.count : row.totalAmount;
  if (value <= 0) return 0;
  return Math.min(100, (value / maxMetric(rows, metric)) * 100);
}
